import { initStore, getStore } from './index';
import createPersistStore, { PersistStateType } from './persistStore';
import createRootStore, { RootStateType } from './rootStore';
import createUserStore, { UserStateType } from './userStore';
import createMiniCartStore, { IMiniCartState } from './miniCartStore';
import createServerStore, { ServerStateType } from './serverStore';
import createPopupFixedStore, { IPopupFixedState, } from './popupFixedStore';
import { IParsedAppContext } from 'base/utils/parse-app-context';
import { AppContext } from 'next/app';
import { RouteEnum } from 'pro/config/routers';
import { isEmpty } from 'ava-util/dist/data';
import { COOKIE_IS_GUEST_USER, DEBUG_NODE_CACHE } from 'base/config/constant';

export type StorePropsType = {
    rootState: RootStateType
    persistState: PersistStateType;
    userState: UserStateType;
    miniCartSate: IMiniCartState;
    serverState?: ServerStateType; // 非 cache 页面不传递 serverState 数据
    popupFixedState?: IPopupFixedState;
}

export const initLoadStore = async (parsed: IParsedAppContext, appContext: AppContext): Promise<StorePropsType> => {
    const { query } = parsed;
    const { ctx: { pathname } } = appContext;
    const isCachePage = pathname === RouteEnum.CACHE;
    const isServer = !(process as any).browser;
    const prev = getStore();
    const uninitialized = isServer || isEmpty(prev);

    const store = uninitialized ? initStore({
        rootStore: createRootStore(parsed),
        persistStore: createPersistStore(prev?.persistStore?.state),
        userStore: createUserStore({
            loginToken: parsed.loginToken,
            isGuestUser: !!parsed.cookies[COOKIE_IS_GUEST_USER],
        }), //获取cookie中的登陆信息
        miniCartStore: createMiniCartStore(),
        serverStore: createServerStore(prev?.serverStore?.state, parsed.query[DEBUG_NODE_CACHE]),
        popupFixedStore: createPopupFixedStore(),
    }) : prev;

    const { rootStore, persistStore, userStore, miniCartStore, serverStore, popupFixedStore } = store;

    // 所有api请求都需要在rootStore初始化后进行
    if (uninitialized) {
        // 清缓存
        if (isCachePage) {
            await serverStore.clearState(query.clearKey as string, parsed.envName)
        }

        //所有请求之前先获取当前国家货币信息
        const localeList = await serverStore.fetchLocaleList();
        await rootStore.fetchLocaleInfo(appContext, localeList);

        const { country, lang } = rootStore.state;

        await Promise.all([
            serverStore.fetchState(lang, country),
        ]);

        persistStore.getPersistState(serverStore.state, lang, country);
    } else {
        // 删除本地缓存导致 locale 相关信息丢失
        if (!parsed.country || !parsed.currency || !parsed.lang) {
            rootStore.update({
                ...parsed,
                country: prev.rootStore.state.country || 'US',
                currency: prev.rootStore.state.currency || 'USD',
                lang: prev.rootStore.state.lang || 'en',
            });
        } else {
            rootStore.update(parsed);

        }

        userStore.update({
            loginToken: parsed.loginToken,
            isGuestUser: !!parsed.cookies[COOKIE_IS_GUEST_USER],
        })
    }

    return {
        rootState: rootStore.state,
        persistState: persistStore.state,
        userState: userStore.state,
        miniCartSate: miniCartStore.state,
        serverState: serverStore.state,
        popupFixedState: popupFixedStore.state,
    }
};

export const initComponentStore = (props: StorePropsType) => {
    const prev = getStore();

    return (process as any).browser && props ? initStore({
        rootStore: createRootStore(props.rootState),
        persistStore: createPersistStore(props.persistState),
        serverStore: createServerStore(props.serverState),
        userStore: createUserStore(prev.userStore?.state || props.userState),
        miniCartStore: createMiniCartStore(prev.miniCartStore?.state),
        popupFixedStore: createPopupFixedStore(props?.popupFixedState),
    }) : getStore();
};